import toFixed from 'accounting-js/lib/toFixed.js';
import {
  faLongArrowLeft,
  faLongArrowRight,
} from '@fortawesome/pro-regular-svg-icons';
import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { useObservableState } from 'observable-hooks';
import { map } from 'rxjs/operators';

import { useService } from '../../../services/services';
import Form, { SubmitType } from '../../../components/form';
import Button from '../../../components/form/elements/button';
import Link from '../../../components/form/elements/link';
import Feedingstuffs from './feedingstuffs';
import { LIVESTOCK_FODDER_NEED_INDEX } from '../../../router/routes';
import { route, numberTransform } from '../../../services/utils';

import { FormData } from './create';
import Group from '../../../components/form/elements/group';
import css from './step-feedingstuffs.module.css';
import Classifier from '../../../enums/classifier';
import ClassifierGroup from '../../../enums/classifier-group';
import ClassifierPropertyKey from '../../../enums/classifier-property-key';
import Select, { Option } from '../../../components/form/elements/select';
import Feedingstuff from '../../../interfaces/feedingstuff';
import Text from '../../../components/form/elements/text';
import Disabled from '../../../components/form/elements/disabled';
import Watch from '../../../components/form/elements/watch';
import Integer from '../../../components/form/elements/integer';

const schema = (schema: any, checkIfSelect: Function) => {
  return schema.object().shape({
    feedingstuffs: schema.lazy((value: { [key: string]: Feedingstuff }) => {
      let feedingstuffsSchema = {};
      if (value && Object.keys(value).length > 0) {
        Object.entries(value).forEach(
          ([feedingstuffKey, feedingstuff]: [string, Feedingstuff]) => {
            //feedingstuff validation

            Object.assign(feedingstuffsSchema, {
              [feedingstuffKey]: schema.object().shape({
                feedingstuff_id: yup.string().uuid().required(),
                title: checkIfSelect(feedingstuff?.feedingstuff_id)
                  ? yup.object().shape({
                      value: yup.string().required(),
                    })
                  : yup.string().required(),
                dry: yup.number().min(0).required(),
                yield_avg: yup
                  .number()
                  .positive()
                  .nullable()
                  .transform(numberTransform),
                planned_land_ha: yup
                  .number()
                  .positive()
                  .nullable()
                  .transform(numberTransform),
                planned_to_produce_t: yup
                  .number()
                  .min(0)
                  .decimal()
                  .nullable()
                  .transform(numberTransform),
                price_eur_t: yup
                  .number()
                  .positive()
                  .nullable()
                  .transform(numberTransform),
              }),
            });
          }
        );

        return schema.object().shape(feedingstuffsSchema);
      }
      return schema
        .object()
        .shape({})
        .test({
          test: (obj: any) => false,
        });
    }),
  });
};

export interface FeedingstuffsSubmitData {
  feedingstuffs: { [key: string]: Feedingstuff };
}

export default function StepFeedingstuffs({
  formData,
  onReturn,
  onSubmit,
  saveFormData,
}: {
  formData: FormData;
  onReturn: () => void;
  onSubmit: SubmitType<FeedingstuffsSubmitData>;
  saveFormData: Function;
}) {
  const { t } = useTranslation();
  const tp = useCallback(
    (key: string) => t('pages.livestock.fodder_need.'.concat(key)),
    [t]
  );
  const { farmId } = useParams<{ farmId: string }>();

  const [body, setBody] = useState([]);
  const { classifierService } = useService();
  const [classifiers] = useObservableState<Option[]>(
    () =>
      classifierService
        .values(ClassifierGroup.WAREHOUSE, Classifier.PRODUCT_TYPES, {
          [ClassifierPropertyKey.DRY_MATTER]: true,
        })
        .pipe(
          map((values) =>
            values.map((value) => {
              return {
                value: value.id,
                label: value.value,
                parentValue: value.parent.value,
                properties: value.properties,
              };
            })
          )
        ),
    null
  );

  const showSelect = useCallback(
    (feedingstuff?: string) => {
      if (feedingstuff && classifiers) {
        const selectedClassifier = classifiers.find(
          (obj: any) => obj.value === feedingstuff
        );
        const feedingstuffProperty = Object.values(
          selectedClassifier.properties
        ).find(
          (obj: any) => obj.property.key === ClassifierPropertyKey.FEEDINGSTUFF
        ) as any;
        return feedingstuffProperty.value;
      }
      return true;
    },
    [classifiers]
  );

  const getTitles = useCallback(
    (
      distinctFeedingstuffs: Option[],
      getValues: Function,
      parentName: string,
      value?: string
    ) => {
      const selectedClassifier = distinctFeedingstuffs.find(
        (obj: any) => obj.value === getValues(parentName + '.feedingstuff_id')
      );

      return classifiers
        .filter((option) => {
          const matchesValue =
            !value ||
            option.parentValue
              .toLowerCase()
              .includes(value.toLowerCase().trim());
          const matchesClassifier = selectedClassifier?.label === option.label;
          return matchesValue && matchesClassifier;
        })
        .map((option) => ({
          value: option.parentValue,
          label: option.parentValue,
        }));
    },
    [classifiers]
  );

  useEffect(() => {
    if (classifiers) {
      const distinctFeedingstuffsClassifiers = classifiers.filter(
        (value: any, index: any, self: any) =>
          self.map((x: any) => x.label).indexOf(value.label) === index
      );

      if (formData.feedingstuffsClassifiers.length === 0) {
        //change title property structure if first time load and on update
        if (formData.id) {
          Object.entries(formData.feedingstuffs).forEach(
            ([feedingstuffKey, feedingstuff]: [string, Feedingstuff]) => {
              if (
                showSelect(
                  formData.feedingstuffs[feedingstuffKey].feedingstuff_id
                )
              ) {
                formData.feedingstuffs[feedingstuffKey].title = {
                  label: formData.feedingstuffs[feedingstuffKey].title,
                  value: formData.feedingstuffs[feedingstuffKey].title,
                };
              }
            }
          );
        }

        //set classifiers in form data for using in net steps
        saveFormData({
          feedingstuffsClassifiers: distinctFeedingstuffsClassifiers,
          feedingstuffs: formData.feedingstuffs,
        });
      }

      setBody([
        Group(
          [
            Feedingstuffs(
              {
                name: 'feedingstuffs',
                components: () => [
                  Watch(
                    Select('feedingstuff_id', {
                      options: distinctFeedingstuffsClassifiers,
                      clearable: false,
                    }),
                    ({ value: [value], parentName, setValue, getValues }) => {
                      setValue(parentName + '.title', '');
                      const selectedClassifier = classifiers.find(
                        (obj: Option) => {
                          return obj.value === value;
                        }
                      );
                      if (selectedClassifier) {
                        const dryProperty = Object.values(
                          selectedClassifier.properties
                        ).find(
                          (obj: any) =>
                            obj.property.key ===
                            ClassifierPropertyKey.DRY_MATTER
                        ) as any;

                        setValue(parentName + '.dry', dryProperty.value);
                      }
                    }
                  ),
                  Select('title', {
                    asyncOptions: async (
                      value,
                      loadedOptions,
                      { parentName, getValues, setValue }
                    ) => {
                      return {
                        hasMore: false,
                        options: getTitles(
                          distinctFeedingstuffsClassifiers,
                          getValues,
                          parentName,
                          value
                        ),
                        additional: {},
                      };
                    },
                    cacheOn: ['feedingstuff_id'],
                    multi: false,
                    label: t('pages.warehouse.products.unit'),
                  }),
                  Text('title', null, null),
                  Integer('dry', null, null),
                  Watch(
                    Integer('yield_avg', null, null, false),
                    ({ value, parentName, setValue, getValues }) => {
                      setValue(
                        parentName + '.planned_to_produce_t',
                        parseFloat(
                          toFixed(
                            value * getValues(parentName + '.planned_land_ha'),
                            3,
                            { shouldDirty: true }
                          )
                        )
                      );
                    }
                  ),
                  Watch(
                    Integer('planned_land_ha', null, null, false),
                    ({ value, parentName, setValue, getValues }) => {
                      setValue(
                        parentName + '.planned_to_produce_t',
                        parseFloat(
                          toFixed(
                            getValues(parentName + '.yield_avg') * value,
                            3,
                            { shouldDirty: true }
                          )
                        )
                      );
                    }
                  ),
                  Disabled(Integer('planned_to_produce_t', null, null, false)),
                  Integer('price_eur_t', null, null, false),
                ],
                showSelect: showSelect,
              },
              classifiers
            ),
          ],
          tp('feedingstuffs_and_sources')
        ),
      ]);
    }
  }, [
    classifiers,
    setBody,
    getTitles,
    showSelect,
    t,
    tp,
    formData.feedingstuffsClassifiers.length,
    saveFormData,
    formData.feedingstuffs,
    formData.id,
  ]);

  const [footer] = useState(() => [
    Button('return', onReturn, tp('return'), faLongArrowLeft, null, true),
    Link(route(LIVESTOCK_FODDER_NEED_INDEX, { farmId: farmId }), 'cancel'),
    Button('continue', null, tp('continue'), faLongArrowRight),
  ]);

  return (
    <div className={css['step-feedingstuffs']}>
      <Form
        title={tp('fodder_need_calculation')}
        mode="step"
        body={body}
        footer={footer}
        values={formData}
        submit={onSubmit}
        schema={schema(yup, showSelect)}
      />
    </div>
  );
}
